import React from "react";
import { useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import {
  CREATE_USER_FAMILY_INFO,
  UPDATE_USER_FAMILY_INFO,
} from "@/GraphQL/Mutation";
import { GET_ALL_USER_RELATIONS } from "@/GraphQL/Queries";
import ModalLayout from "@/shared/components/Layouts/ModalLayout";
import CreateLayout from "@/shared/components/Layouts/CreateLayout";
import { educationOptions, findOption } from "@/utils/helpers";

export default function FamilyInfoModal({
  modal,
  toggle,
  userId,
  defaultData,
  refetch,
}) {
  const { data } = useQuery(GET_ALL_USER_RELATIONS);

  const relationOptions =
    data?.getAllUserRelations?.map((rel) => ({
      label: rel.relation,
      value: rel.id,
    })) || [];

  const diedOption = [
    { label: "No", value: false },
    { label: "Yes", value: true },
  ];

  const [createFamilyInfo, { loading }] = useMutation(CREATE_USER_FAMILY_INFO);
  const [updateFamilyInfo, { loading: updateLoading }] = useMutation(
    UPDATE_USER_FAMILY_INFO
  );

  const handleSubmit = async (formData) => {
    const variables = {
      name: formData.name,
      mobile: formData.mobile,
      relation_id: parseInt(formData.relation_id.value),
      is_died: formData.is_died.value,
      user_id: parseInt(userId),
      date_of_birth: formData.date_of_birth || null,
      education_level: formData.education_level?.value || null,
    };

    try {
      if (defaultData?.id) {
        await updateFamilyInfo({
          variables: {
            id: defaultData.id,
            ...variables,
          },
        });
        refetch();
        toast.success("Family info updated successfully!");
      } else {
        await createFamilyInfo({ variables });
        refetch();
        toast.success("Family info saved successfully!");
      }

      toggle();
    } catch (err) {
      console.error("Error:", err);
      toast.error("Failed to save family info.");
    }
  };

  const forms = [
    {
      name: "name",
      placeholder: "Name",
      rules: () => ({ required: "This is required field" }),
      label: "Name",
      type: "text",
      deviceSize: 12,
      defaultValue: defaultData?.name || "",
    },
    {
      name: "relation_id",
      placeholder: "Select Element",
      rules: () => ({ required: "This is required field" }),
      label: "Select Relation",
      type: "select",
      deviceSize: 12,
      options: relationOptions,
      defaultValue: defaultData?.relation_id
        ? findOption(relationOptions, defaultData.relation_id)
        : "",
    },
    {
      name: "mobile",
      placeholder: "Mobile",
      label: "Mobile (optional)",
      type: "mobile",
      deviceSize: 12,
      defaultValue: defaultData?.mobile || "",
      // isDisplayed: ({ watch }) => {
      //   const relation = watch("relation_id");
      //   return !(relation?.value === 5 || relation?.value === 6);
      // },
    },
    {
      name: "date_of_birth",
      placeholder: "Select Date",
      label: "Date Of Birth (optional)",
      type: "date",
      deviceSize: 12,
      defaultValue: defaultData?.date_of_birth || "",
      minDate: new Date("1900-01-01"),
      maxDate: new Date(),
    },
    {
      name: "is_died",
      placeholder: "Select",
      label: "Late (optional)",
      type: "select",
      deviceSize: 12,
      options: diedOption,
      defaultValue:
        typeof defaultData?.is_died === "boolean"
          ? findOption(diedOption, defaultData.is_died)
          : "",
      isDisplayed: ({ watch }) => {
        const relation = watch("relation_id");
        return !(relation?.value === 5 || relation?.value === 6);
      },
    },
    {
      name: "education_level",
      placeholder: "Select Education Level",
      rules: () => ({ required: "This is required field" }),
      label: "Education Level",
      type: "select",
      deviceSize: 12,
      options: educationOptions,
      defaultValue: defaultData?.education_level
        ? findOption(educationOptions, defaultData.education_level)
        : "",
      isDisplayed: ({ watch }) => {
        const relation = watch("relation_id");
        return relation?.value === 5 || relation?.value === 6;
      },
    },
  ];

  return (
    <ModalLayout
      style={{ maxWidth: "500px", width: "100%" }}
      className="modal-sm-custom"
      modal={modal}
      toggle={toggle}
    >
      <CreateLayout
        onSubmit={handleSubmit}
        forms={forms}
        toggle={toggle}
        loading={loading || updateLoading}
        submitText={defaultData?.id ? "Update" : "Add"}
      />
    </ModalLayout>
  );
}
